import { chakra } from '@chakra-ui/react';
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { FieldError } from './FieldError';

const Root = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', gap: '1.5', minWidth: 0 },
});
const Label = chakra('label', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1',
    fontFamily: 'body',
    fontSize: 'sm',
    fontWeight: 'medium',
    color: 'text.primary',
  },
});
const Help = chakra('p', {
  base: { margin: 0, fontFamily: 'body', fontSize: 'sm', lineHeight: 'snug', color: 'text.muted' },
});

/** Props FormField injects into its control child (all our inputs accept these). */
interface InjectedProps {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  invalid?: boolean;
  required?: boolean;
}

export interface FormFieldProps {
  label: ReactNode;
  /** The control (TextInput, Select, TextArea, …). */
  children: ReactElement<InjectedProps>;
  help?: ReactNode;
  /** Error message — when set, wires aria-invalid + describedby and renders a FieldError. */
  error?: ReactNode;
  required?: boolean;
  /** Visually hide the label but keep it for screen readers. */
  hideLabel?: boolean;
  /** Override the generated control id. */
  id?: string;
  /** Optional test id applied to the help text node (for e2e/unit selectors). */
  helpTestId?: string;
}

/**
 * Accessible field wrapper: associates a label, optional help text and an error with
 * a single control via `htmlFor`/`id` and `aria-describedby`, and sets `aria-invalid`.
 */
export function FormField({
  label,
  children,
  help,
  error,
  required,
  hideLabel,
  id: idProp,
  helpTestId,
}: FormFieldProps) {
  const autoId = useId();
  const id = idProp ?? `field-${autoId}`;
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const hasError = error != null && error !== '';
  const describedBy =
    [help != null && help !== '' ? helpId : null, hasError ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': hasError || undefined,
        invalid: hasError || undefined,
        required,
      })
    : children;

  return (
    <Root>
      <Label
        htmlFor={id}
        css={
          hideLabel
            ? {
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
                borderWidth: 0,
              }
            : undefined
        }
      >
        {label}
        {required && (
          <chakra.span aria-hidden color="status.danger">
            *
          </chakra.span>
        )}
      </Label>
      {control}
      {help != null && help !== '' && !hasError && (
        <Help id={helpId} data-testid={helpTestId}>
          {help}
        </Help>
      )}
      {hasError && <FieldError id={errorId}>{error}</FieldError>}
    </Root>
  );
}
